import React from 'react';
import { Link } from 'react-router-dom';

const s = {
  page: { maxWidth: 800, margin: '0 auto', padding: '40px 20px', color: 'var(--text)' },
  hero: { background: 'var(--primary)', borderRadius: 16, padding: '36px 40px', marginBottom: 40, color: '#fff' },
  heroTitle: { fontSize: 32, fontWeight: 800, marginBottom: 8 },
  heroSub: { fontSize: 15, opacity: 0.85 },
  section: { background: 'var(--surface)', borderRadius: 14, padding: '28px 32px', marginBottom: 20, boxShadow: 'var(--shadow)' },
  h2: { fontSize: 20, fontWeight: 700, color: 'var(--heading)', marginBottom: 14, marginTop: 0 },
  p: { color: 'var(--text2)', fontSize: 15, lineHeight: 1.7, marginBottom: 12, marginTop: 0 },
  ul: { color: 'var(--text2)', fontSize: 15, lineHeight: 1.8, paddingLeft: 22, marginBottom: 12, marginTop: 0 },
  highlight: { background: '#fefce8', color: '#92400e', borderRadius: 10, padding: '14px 18px', fontSize: 14, fontWeight: 600, marginBottom: 20, borderLeft: '4px solid #f59e0b' },
  infoBox: { background: '#eff6ff', color: '#1e40af', borderRadius: 10, padding: '14px 18px', fontSize: 14, marginBottom: 12, borderLeft: '4px solid #3b82f6' },
  steps: { counterReset: 'step', listStyle: 'none', paddingLeft: 0 },
  stepItem: { display: 'flex', gap: 16, marginBottom: 20, alignItems: 'flex-start' },
  stepNum: { background: 'var(--primary)', color: '#fff', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, flexShrink: 0 },
  stepText: { paddingTop: 4 },
  stopBox: { background: '#f0fdf4', color: '#166534', borderRadius: 10, padding: '16px 20px', fontSize: 15, fontWeight: 600, borderLeft: '4px solid #22c55e', marginBottom: 12 },
  footer: { textAlign: 'center', color: 'var(--text4)', fontSize: 14, marginTop: 40, paddingTop: 24, borderTop: '1px solid var(--border)' },
  footerLink: { color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 },
  backLink: { color: 'var(--primary)', fontWeight: 600, fontSize: 15, marginBottom: 32, display: 'inline-block', textDecoration: 'none' },
};

export default function SmsOptIn() {
  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <div style={s.page}>

        <Link to="/" style={s.backLink}>← Back to Home</Link>

        <div style={s.hero}>
          <div style={s.heroTitle}>📱 SMS Opt-In Information</div>
          <div style={s.heroSub}>How SeniorCare App sends SMS reminders and alerts</div>
        </div>

        {/* Program Description */}
        <div style={s.section}>
          <h2 style={s.h2}>About Our SMS Program</h2>
          <p style={s.p}>
            SeniorCare App (<strong>myseniorcare.org</strong>) sends SMS text message reminders and
            emergency alerts to seniors and caregivers who have explicitly opted in to receive them.
          </p>
          <p style={s.p}>
            Our SMS program is operated using the toll-free number <strong>+1 (877) 335-1850</strong>.
          </p>

          <div style={s.infoBox}>
            <strong>Message types we send:</strong>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>Medication reminders (e.g. "Time to take: Aspirin 100mg")</li>
              <li>Appointment reminders (24 hours and 1 hour before)</li>
              <li>Emergency SOS alerts (triggered manually by the account holder)</li>
              <li>SMS opt-in confirmation messages</li>
            </ul>
          </div>

          <p style={s.p}>
            <strong>Message frequency:</strong> Varies based on the number of medications and appointments
            configured. Typically 1–10 messages per day per user.
          </p>
          <p style={s.p}>
            <strong>Message &amp; data rates may apply.</strong>
          </p>
        </div>

        {/* How Opt-In Works */}
        <div style={s.section}>
          <h2 style={s.h2}>How to Opt In</h2>

          <div style={s.highlight}>
            SMS reminders are a completely <strong>optional feature</strong>. Seniors and caregivers can
            use all features of SeniorCare — including medications, appointments, vitals, and journals —
            without ever enrolling in SMS. No phone number is ever enrolled automatically.
          </div>

          <p style={s.p}>
            To enable SMS reminders, the account holder must explicitly send an SMS invite from within the app:
          </p>

          <ol style={s.steps}>
            <li style={s.stepItem}>
              <div style={s.stepNum}>1</div>
              <div style={s.stepText}>
                <strong>Register an account</strong> at <a href="https://myseniorcare.org/register" style={{ color: 'var(--primary)' }}>myseniorcare.org/register</a>
                and add a Senior or Caregiver profile. A phone number may be stored for contact purposes — no SMS is sent at this stage.
              </div>
            </li>
            <li style={s.stepItem}>
              <div style={s.stepNum}>2</div>
              <div style={s.stepText}>
                <strong>Choose to send an invite.</strong> On the Senior or Caregiver detail page, the account
                holder clicks the <strong>"Send SMS Invite"</strong> button. This is a deliberate, optional
                action — it is never triggered automatically.
              </div>
            </li>
            <li style={s.stepItem}>
              <div style={s.stepNum}>3</div>
              <div style={s.stepText}>
                <strong>The recipient receives a single opt-in message</strong> asking them to reply
                <strong> YES</strong> to consent to reminders, or <strong>STOP</strong> to decline.
              </div>
            </li>
            <li style={s.stepItem}>
              <div style={s.stepNum}>4</div>
              <div style={s.stepText}>
                <strong>Only after replying YES</strong> will the recipient receive any medication reminders,
                appointment alerts, or SOS notifications. Consent is stored securely and can be revoked at any time by replying STOP.
              </div>
            </li>
          </ol>

          <div style={s.infoBox}>
            No SMS messages are sent to any phone number until the recipient has explicitly replied YES.
            SMS enrollment is entirely optional and has no effect on access to any app features.
          </div>
        </div>

        {/* How to Opt Out */}
        <div style={s.section}>
          <h2 style={s.h2}>How to Opt Out</h2>
          <p style={s.p}>You can stop receiving SMS messages at any time using any of these methods:</p>

          <div style={s.stopBox}>
            📵 Reply <strong>STOP</strong> to any message from +1 (877) 335-1850 to immediately unsubscribe.
          </div>

          <ul style={s.ul}>
            <li>Reply <strong>STOP</strong> to any SMS — you will be immediately unsubscribed</li>
            <li>Reply <strong>HELP</strong> to any SMS — you will receive support contact information</li>
            <li>Contact us at <a href="mailto:support@myseniorcare.org" style={{ color: 'var(--primary)' }}>support@myseniorcare.org</a></li>
            <li>Log in to your account and remove your phone number from your Senior or Caregiver profile</li>
          </ul>

          <p style={s.p}>
            After opting out, you will receive one final confirmation SMS. No further messages will be sent.
          </p>
        </div>

        {/* Contact & Support */}
        <div style={s.section}>
          <h2 style={s.h2}>Help &amp; Support</h2>
          <p style={s.p}>If you need help with SMS messages or have questions about our program:</p>
          <ul style={s.ul}>
            <li>Email: <a href="mailto:support@myseniorcare.org" style={{ color: 'var(--primary)' }}>support@myseniorcare.org</a></li>
            <li>Website: <a href="https://myseniorcare.org" style={{ color: 'var(--primary)' }}>myseniorcare.org</a></li>
            <li>SMS sender number: +1 (877) 335-1850</li>
          </ul>
        </div>

        <div style={s.footer}>
          <Link to="/privacy" style={s.footerLink}>Privacy Policy</Link>
          {' · '}
          <Link to="/terms" style={s.footerLink}>Terms of Service</Link>
          {' · '}
          <span>© 2026 SeniorCare App</span>
        </div>

      </div>
    </div>
  );
}
